import type { PoolCabinet } from "@hakili/contracts";
import { formatMoney, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { useEffect, useState } from "react";

import { PartnerAddModal, PartnerTabHeader } from "./partner-tabs.js";

/**
 * `round CN-A`, `CN-A.3`: **the cabinets' directory and onboarding**, where the
 * drawing places them , *« L'annuaire et l'onboarding des cabinets vivent dans
 * l'onglet Partenaires, côté administration »* , so a cabinet can be opened
 * on staging by a screen. Two doors, in the walk's own order: the
 * organisation by `ouvrirOrganisation`'s (`type: "cabinet"`), then the cabinet
 * by `onboarderCabinet`'s (`P1-EXP-002`), with its bank, its capacity and its
 * two checks. The load per week and month the drawing prints beside each
 * cabinet is `WF-CON-25`'s figure and has no door here: a dash.
 */

/** One row of the scheduling pool, exactly as `GET /expertise/cabinets` sends it (`CABINET-LIST-FEE`: the honoraires ride on it). */
export type CabinetView = PoolCabinet;

/** `CN-B.2`: the cabinet's organisation, for the invitation's state on the card. */
export interface CabinetOrganisationView {
  readonly id: string;
  readonly status?: string;
  readonly administrator?: { readonly fullName: string; readonly state: string } | null;
}

export interface CabinetsDoors {
  readonly readCabinets: () => Promise<readonly CabinetView[]>;
  readonly openCabinet: (input: {
    name: string;
    contactName: string;
    phone: string;
    riverBank: "gauche" | "droite";
    zoneLabel: string;
    accreditation: string;
    feesLabel: string;
    defaultDailyCapacity: number;
    feeAmount: number | null;
    identityVerified: boolean;
    contractReceived: boolean;
  }) => Promise<void>;
  /** `round C5-2`, `M4`: the fiche's terms , the fee and the contractual minimum , changed from the row. */
  readonly amendTerms?: (input: {
    cabinetId: string;
    feeAmount: number | null;
    minimumCapacity: number;
  }) => Promise<void>;
  /**
   * `round C5-2`, `M1`: « Inviter le responsable », lit , identity's
   * first-admin door on the cabinet's organisation (02 §2.8.2 step 3), the
   * link leaving for expert-web. `CN-B` built it greyed to « C5 ».
   */
  readonly inviteResponsible?: (input: {
    organisationId: string;
    fullName: string;
    email: string;
    phone: string;
  }) => Promise<void>;
}

const DASH = t("common.valeur.tiret");
const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

export function Cabinets(
  props: CabinetsDoors & { readonly organisations?: readonly CabinetOrganisationView[] },
): React.JSX.Element {
  const [cabinets, setCabinets] = useState<readonly CabinetView[] | null>(null);
  const [inviting, setInviting] = useState<CabinetView | null>(null);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteRefusal, setInviteRefusal] = useState<string | null>(null);
  const [inviteWord, setInviteWord] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [riverBank, setRiverBank] = useState<"gauche" | "droite">("gauche");
  const [zoneLabel, setZoneLabel] = useState("");
  const [accreditation, setAccreditation] = useState("");
  const [feesLabel, setFeesLabel] = useState("");
  const [feeAmount, setFeeAmount] = useState("");
  const [capacity, setCapacity] = useState("8");
  const [terms, setTerms] = useState<{ cabinetId: string; fee: string; minimum: string } | null>(null);
  const [termsWord, setTermsWord] = useState<string | null>(null);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [contractReceived, setContractReceived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const { readCabinets } = props;

  useEffect(() => {
    const run: { live: boolean } = { live: true };
    void readCabinets()
      .then((rows) => {
        if (run.live) setCabinets(rows);
      })
      .catch((failure: unknown) => {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      });
    return () => {
      run.live = false;
    };
  }, [readCabinets, epoch]);

  /**
   * `FX-3`, `M5` (`S2-10`): the missing fields, NAMED. « Onboarder le cabinet » was greyed with one blanket
   * sentence , *« Chaque champ est requis »* , which tells a founder holding a half-filled form nothing about
   * which half. The list is derived from the same conditions that gate the button, so the two cannot drift.
   *
   * `round SMALL-FINDINGS`, `M2`: the honoraires LIBELLÉ is not in the list. It computes nothing (its hint
   * says so), nothing downstream reads it, and its sibling, the FCFA amount, is optional by `D2` , an empty
   * amount is the platform default. A required label beside an optional figure let the form demand the
   * words and not the number.
   */
  const missing: string[] = [
    ...(name.trim() === "" ? [t("console.cabinets.champNom")] : []),
    ...(contactName.trim() === "" ? [t("console.cabinets.champContact")] : []),
    ...(phone.trim() === "" ? [t("console.cabinets.champTelephone")] : []),
    ...(zoneLabel.trim() === "" ? [t("console.cabinets.champZone")] : []),
    ...(accreditation.trim() === "" ? [t("console.cabinets.champAgrement")] : []),
    ...(Number.isInteger(Number(capacity)) && Number(capacity) > 0
      ? []
      : [t("console.cabinets.champCapacite")]),
  ];
  const complete = missing.length === 0;
  const missingWords = t("console.cabinets.incompletChamps").replace("{champs}", missing.join(", "));

  return (
    <div data-testid="cabinets" className="flex flex-col gap-3">
      {/* The founder's decision: the tab shows its partners; the ONE
          « Ajouter » design at the right of the sub-header, above the list,
          outside any card, opens the form over the veil. */}
      <PartnerTabHeader
        title={t("console.cabinets.titre")}
        subtitle={t("console.cabinets.sousTitre")}
        control={{
          label: t("console.cabinets.ouvrir"),
          onClick: () => {
            setOpen(true);
          },
        }}
      />
      <section className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
        {/* `round CN-B`, `CN-B.2`: the drawing's own columns (`ExpertsDirectory`):
            cabinet and contact, zone and accreditation, fees, the capacity per
            day, the REAL load per week and month from the tours, the status ,
            and « Inviter le responsable », built greyed: the founder's decision
            of 12 September, « l'espace du cabinet arrive avec C5 ». */}
        <div className="flex items-center gap-4 border-b border-gray-100 pb-2.5">
          <span className="w-44 text-xs text-gray-500">{t("console.cabinets.colCabinet")}</span>
          <span className="flex-1 text-xs text-gray-500">{t("console.cabinets.colZone")}</span>
          <span className="w-36 text-xs text-gray-500">{t("console.cabinets.colHonoraires")}</span>
          <span className="w-20 text-right text-xs text-gray-500">{t("console.cabinets.colParJour")}</span>
          <span className="w-36 text-right text-xs text-gray-500">{t("console.cabinets.colCharge")}</span>
          <span className="w-36 text-right text-xs text-gray-500">{t("console.cabinets.colStatut")}</span>
        </div>
        {cabinets === null ? null : cabinets.length === 0 ? (
          <p className="mt-3 text-xs text-gray-500">{t("console.cabinets.aucun")}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {cabinets.map((cabinet) => {
              const organisation = props.organisations?.find((one) => one.id === cabinet.organisationId);
              const invitation = organisation?.administrator ?? null;
              const invitationWords =
                invitation === null
                  ? t("console.cabinets.responsableAInviter")
                  : invitation.state === "active"
                    ? fill("console.cabinets.responsableActif", { nom: invitation.fullName })
                    : fill("console.cabinets.responsableInvite", { nom: invitation.fullName });
              return (
                <li key={cabinet.id} data-testid={`cabinet-${cabinet.id}`} className="py-3">
                  <div className="flex items-center gap-4">
                    <div className="w-44 shrink-0">
                      <p className="text-sm font-medium text-gray-900">{cabinet.name}</p>
                      <p className="truncate text-xs text-gray-500">{cabinet.contactName}</p>
                      <p className="truncate text-xs text-gray-500">{cabinet.phone}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-gray-700">{cabinet.zoneLabel}</p>
                      <p data-testid="cabinet-invitation" className="truncate text-xs text-gray-500">
                        {invitationWords}
                      </p>
                    </div>
                    <span className="w-36 text-xs text-gray-700" data-testid="cabinet-honoraires">
                      {cabinet.feeAmount === null ? DASH : formatMoney(cabinet.feeAmount)}
                    </span>
                    <span className="w-20 shrink-0 text-right text-sm tabular-nums">
                      {String(cabinet.defaultDailyCapacity)}
                    </span>
                    <span
                      data-testid="cabinet-charge"
                      className="w-36 shrink-0 text-right text-xs text-gray-500 tabular-nums"
                    >
                      {fill("console.cabinets.chargeSemaineMois", {
                        semaine: String(cabinet.loadWeek),
                        mois: String(cabinet.loadMonth),
                      })}
                    </span>
                    <span className="flex w-36 shrink-0 justify-end">
                      <span
                        className={
                          "rounded-full px-2 py-0.5 text-xs font-medium " +
                          (cabinet.active ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")
                        }
                      >
                        {cabinet.active ? t("console.cabinets.actif") : t("console.cabinets.enVerification")}
                      </span>
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {/* `PARTNERS-DONE-STATE` (D-1019-3): a responsable already invited is a done state; check (h) reads it. */}
                    <span
                      data-testid={`cabinet-${cabinet.id}-responsable`}
                      data-row-state={invitation === null ? "pending" : "done"}
                      className="contents"
                    >
                      {/* `round C5-2`, `M1`: lit when the cabinet has no responsable yet
                        and the door is wired; greyed with its reason otherwise. */}
                      {invitation !== null ? null : props.inviteResponsible !== undefined ? (
                        <button
                          type="button"
                          data-testid="cabinet-inviter"
                          onClick={() => {
                            setInviting(cabinet);
                            setInviteRefusal(null);
                            setInviteWord(null);
                          }}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                        >
                          {t("console.cabinets.inviterResponsable")}
                        </button>
                      ) : (
                        <GuardedButton
                          disabled
                          reason={t("console.cabinets.inviterMotif")}
                          variant="secondary"
                          testId="cabinet-inviter"
                        >
                          {t("console.cabinets.inviterResponsable")}
                        </GuardedButton>
                      )}
                    </span>
                    {props.amendTerms === undefined ? null : terms?.cabinetId === cabinet.id ? (
                      <span className="flex items-center gap-2" data-testid="cabinet-termes-formulaire">
                        <input
                          value={terms.fee}
                          onChange={(event) => {
                            setTerms({ ...terms, fee: event.target.value.replace(/\D/gu, "") });
                          }}
                          placeholder={t("console.cabinets.honorairesMontant")}
                          className="w-28 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                          data-testid="cabinet-termes-honoraires"
                        />
                        <input
                          value={terms.minimum}
                          onChange={(event) => {
                            setTerms({ ...terms, minimum: event.target.value.replace(/\D/gu, "") });
                          }}
                          placeholder={t("console.cabinets.capaciteJour")}
                          className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                          data-testid="cabinet-termes-minimum"
                        />
                        <button
                          type="button"
                          data-testid="cabinet-termes-enregistrer"
                          onClick={() => {
                            const door = props.amendTerms;
                            if (door === undefined) return;
                            void (async () => {
                              try {
                                await door({
                                  cabinetId: cabinet.id,
                                  feeAmount: terms.fee === "" ? null : Number(terms.fee),
                                  minimumCapacity:
                                    terms.minimum === ""
                                      ? cabinet.defaultDailyCapacity
                                      : Number(terms.minimum),
                                });
                                setTermsWord(t("console.cabinets.termesEnregistres"));
                                setTerms(null);
                                setEpoch((now) => now + 1);
                              } catch (failure) {
                                setTermsWord(
                                  (failure as { message?: string } | null)?.message ??
                                    t("common.error.connexionEchouee"),
                                );
                              }
                            })();
                          }}
                          className="rounded-lg bg-indigo-600 px-3 py-1 text-xs font-medium text-white"
                        >
                          {t("console.cabinets.enregistrer")}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid="cabinet-termes"
                        onClick={() => {
                          setTerms({
                            cabinetId: cabinet.id,
                            fee: cabinet.feeAmount === null ? "" : String(cabinet.feeAmount),
                            minimum: String(cabinet.defaultDailyCapacity),
                          });
                          setTermsWord(null);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                      >
                        {t("console.cabinets.modifierTermes")}
                      </button>
                    )}
                    {termsWord !== null && terms === null ? (
                      <span className="text-xs text-gray-500" data-testid="cabinet-termes-mot">
                        {termsWord}
                      </span>
                    ) : null}
                    {inviteWord !== null && inviting === null ? (
                      <span className="text-xs text-emerald-700" data-testid="cabinet-inviter-mot">
                        {inviteWord}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {refusal === null ? null : (
          <p role="alert" data-testid="cabinets-refus" className="mt-3 text-sm text-rose-600">
            {refusal}
          </p>
        )}
      </section>

      <PartnerAddModal
        open={open}
        title={t("console.cabinets.ouvrir")}
        onClose={() => {
          setOpen(false);
        }}
      >
        <form
          data-testid="cabinets-formulaire"
          className="grid grid-cols-2 gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void (async () => {
              setBusy(true);
              setRefusal(null);
              try {
                await props.openCabinet({
                  name: name.trim(),
                  contactName: contactName.trim(),
                  phone: phone.trim(),
                  riverBank,
                  zoneLabel: zoneLabel.trim(),
                  accreditation: accreditation.trim(),
                  feesLabel: feesLabel.trim(),
                  defaultDailyCapacity: Number(capacity),
                  feeAmount: feeAmount.trim() === "" ? null : Number(feeAmount),
                  identityVerified,
                  contractReceived,
                });
                setOpen(false);
                setName("");
                setEpoch((now) => now + 1);
              } catch (failure) {
                setRefusal(
                  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                );
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          <Field label={t("console.cabinets.nom")} value={name} onChange={setName} testId="cabinet-nom" />
          <Field label={t("console.cabinets.contact")} value={contactName} onChange={setContactName} />
          <Field label={t("console.cabinets.telephone")} value={phone} onChange={setPhone} />
          <label className="text-xs text-gray-600">
            {t("console.cabinets.rive")}
            <select
              value={riverBank}
              onChange={(event) => {
                setRiverBank(event.target.value === "droite" ? "droite" : "gauche");
              }}
              className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
            >
              <option value="gauche">{t("console.cabinets.riveGauche")}</option>
              <option value="droite">{t("console.cabinets.riveDroite")}</option>
            </select>
          </label>
          <Field
            label={t("console.cabinets.zone")}
            value={zoneLabel}
            onChange={setZoneLabel}
            testId="cabinet-zone"
            hint={t("console.cabinets.zoneAide")}
          />
          <Field
            label={t("console.cabinets.agrement")}
            value={accreditation}
            onChange={setAccreditation}
            testId="cabinet-agrement"
            hint={t("console.cabinets.agrementAide")}
          />
          <Field
            label={t("console.cabinets.honoraires")}
            value={feesLabel}
            onChange={setFeesLabel}
            testId="cabinet-honoraires-libelle"
            hint={t("console.cabinets.honorairesAide")}
          />
          <Field
            label={t("console.cabinets.honorairesMontant")}
            value={feeAmount}
            onChange={(value) => {
              setFeeAmount(value.replace(/\D/gu, ""));
            }}
            testId="cabinet-honoraires-montant"
            // `D2`, entered this round: an amount here is the convention's OVERRIDE of the platform default,
            // and an empty field is the default , which is what the hint says in the founder's own terms.
            hint={t("console.cabinets.honorairesMontantAide")}
          />
          <Field
            label={t("console.cabinets.capaciteJour")}
            value={capacity}
            onChange={setCapacity}
            testId="cabinet-capacite"
            hint={t("console.cabinets.capaciteAide")}
          />
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={identityVerified}
              onChange={(event) => {
                setIdentityVerified(event.target.checked);
              }}
            />
            {t("console.cabinets.identiteVerifiee")}
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={contractReceived}
              onChange={(event) => {
                setContractReceived(event.target.checked);
              }}
            />
            {t("console.cabinets.contratRecu")}
          </label>
          <div className="col-span-2 flex items-center gap-3">
            {complete ? (
              <button
                type="submit"
                data-testid="cabinet-enregistrer"
                disabled={busy}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
              >
                {t("console.cabinets.enregistrer")}
              </button>
            ) : (
              // `FX-3`, `M5` (`S2-10`): « Onboarder le cabinet » was greyed SILENTLY , one blanket sentence
              // about every field. It names what is actually missing now, in the drawing's own words.
              // The SAME testid as the lit button: a control that changes state keeps its name, or a spec can
              // only ever see one of its two states , which is how « greyed silently » went unnoticed.
              <GuardedButton disabled reason={missingWords} variant="primary" testId="cabinet-enregistrer">
                {t("console.cabinets.enregistrer")}
              </GuardedButton>
            )}
            <span className="text-xs text-gray-500">{t("console.cabinets.deuxPortes")}</span>
          </div>
        </form>
        {refusal === null ? null : (
          <p role="alert" className="mt-3 text-sm text-rose-600">
            {refusal}
          </p>
        )}
      </PartnerAddModal>
      <PartnerAddModal
        open={inviting !== null}
        title={t("console.cabinets.inviterTitre")}
        onClose={() => {
          setInviting(null);
        }}
      >
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            const organisationId = inviting?.organisationId;
            const door = props.inviteResponsible;
            if (organisationId === undefined || door === undefined || inviteBusy) return;
            setInviteBusy(true);
            setInviteRefusal(null);
            void (async () => {
              try {
                await door({
                  organisationId,
                  fullName: inviteName,
                  email: inviteEmail,
                  phone: invitePhone,
                });
                setInviteWord(fill("console.cabinets.inviterParti", { email: inviteEmail }));
                setInviting(null);
                setInviteName("");
                setInviteEmail("");
                setInvitePhone("");
                setEpoch((now) => now + 1);
              } catch (failure) {
                setInviteRefusal(
                  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                );
              } finally {
                setInviteBusy(false);
              }
            })();
          }}
        >
          <p className="col-span-2 text-xs text-gray-500">
            {inviting === null ? "" : fill("console.cabinets.inviterPour", { cabinet: inviting.name })}
          </p>
          <Field
            label={t("console.cabinets.inviterNom")}
            value={inviteName}
            onChange={setInviteName}
            testId="cabinet-inviter-nom"
          />
          <Field
            label={t("console.cabinets.inviterEmail")}
            value={inviteEmail}
            onChange={setInviteEmail}
            testId="cabinet-inviter-email"
          />
          <Field
            label={t("console.cabinets.inviterTelephone")}
            value={invitePhone}
            onChange={setInvitePhone}
            testId="cabinet-inviter-telephone"
          />
          <div className="col-span-2 flex items-center gap-3">
            <button
              type="submit"
              data-testid="cabinet-inviter-envoyer"
              disabled={inviteBusy || inviteName.trim() === "" || !inviteEmail.includes("@")}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            >
              {t("console.cabinets.inviterEnvoyer")}
            </button>
            <span className="text-xs text-gray-500">{t("console.cabinets.inviterNote")}</span>
          </div>
        </form>
        {inviteRefusal === null ? null : (
          <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="cabinet-inviter-refus">
            {inviteRefusal}
          </p>
        )}
      </PartnerAddModal>
    </div>
  );
}

/**
 * `FX-3`, `M5` (`S2-10`): the field, with the sentence that makes it readable.
 *
 * The founder's rule after the sitting: *« every field must be understandable without reading the canon »*.
 * `hint` is where that sentence goes , under the input, in grey , and the two fields that looked like each
 * other's duplicates say which of them the platform matches on.
 */
function Field({
  label,
  value,
  onChange,
  testId,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly testId?: string;
  readonly hint?: string;
}): React.JSX.Element {
  return (
    <label className="text-xs text-gray-600">
      {label}
      <input
        data-testid={testId}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
      />
      {hint === undefined ? null : (
        <span className="mt-1 block text-xs leading-snug text-gray-500">{hint}</span>
      )}
    </label>
  );
}
